import React, { useEffect, useMemo, useState } from 'react'
import {
	Autocomplete,
	Box,
	Chip,
	CircularProgress,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
} from '@mui/material'
import { AppCardTitle, AppEmptyLine, AppResultsSkeleton, AppNote } from '@components'

/**
 * Cancellation Rate by Partner (Thibault feedback #6).
 *
 * Companion table under the Cancellation Rate widget. Ordered by
 * cancel_rate descending so the biggest offenders come first. A
 * partner picker lets the operator narrow to a subset when the list
 * is long.
 *
 * Reads GET /api/my-business/cancellation-rate/by-partner with the
 * same filter bundle as the sibling widget (from/to/offices/sipps/duration).
 */
const fmtNum = (n) => new Intl.NumberFormat('es-ES').format(n)
const fmtMoney = (n) =>
	new Intl.NumberFormat('es-ES', {
		style: 'currency', currency: 'EUR', maximumFractionDigits: 0,
	}).format(n)
const fmtPct = (n) =>
	new Intl.NumberFormat('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
		.format(n) + '%'

const rateColor = (rate) =>
	rate >= 30 ? 'error'
	: rate >= 15 ? 'warning'
	: 'success'

export default function CancellationByPartnerTable({
	from, to, offices = [], categories = [], duration = 'total',
}) {
	const [data, setData]       = useState(null)
	const [error, setError]     = useState(null)
	const [loading, setLoading] = useState(true)
	const [selected, setSelected] = useState([])

	useEffect(() => {
		if (!from || !to) return
		setLoading(true); setError(null)
		const qs = new URLSearchParams({ from, to })
		if (offices.length)    qs.set('offices', offices.join(','))
		if (categories.length) qs.set('sipps',   categories.join(','))
		if (duration && duration !== 'total') qs.set('duration', duration)
		fetch(`/api/my-business/cancellation-rate/by-partner?${qs}`,
		      { credentials: 'include' })
			.then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
			.then(setData)
			.catch(e => setError(e.message))
			.finally(() => setLoading(false))
	}, [from, to, offices.join(','), categories.join(','), duration])

	const partners = data?.partners ?? []
	const options  = useMemo(() => partners.map(p => p.partner), [partners])
	const visible  = selected.length === 0
		? partners
		: partners.filter(p => selected.includes(p.partner))

	if (error) return (
		<Paper sx={{ p: 2 }}>
			<AppNote severity="error">Cancellation by partner unreachable: {error}</AppNote>
		</Paper>
	)
	// Loading (also a refetch): the card's skeleton, so nothing can be clicked half-loaded
	if (loading || !data) return <AppResultsSkeleton rows={8}/>
	if (!partners.length) return (
		<Paper sx={{ p: 3 }}>
			<AppEmptyLine>No partners with ≥10 bookings in this window.</AppEmptyLine>
		</Paper>
	)

	return (
		<Paper sx={{ p: 2.5 }}>
			<Stack direction="row" alignItems="center" justifyContent="space-between"
			       flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
				<AppCardTitle icon="handshake-slash" title={`Cancellation by Partner · ${partners.length} partners`}/>
				<Autocomplete multiple size="small"
				              options={options}
				              value={selected}
				              onChange={(_, next) => setSelected(next)}
				              renderInput={(params) => (
				                  <TextField {...params}
				                             placeholder={selected.length ? '' : 'Filter partners…'}/>
				              )}
				              sx={{ minWidth: 320, maxWidth: 520 }}/>
			</Stack>
			<TableContainer sx={{ maxHeight: 500 }}>
				<Table size="small" stickyHeader>
					<TableHead>
						<TableRow>
							<TableCell>Partner</TableCell>
							<TableCell align="right" width={100}>Bookings</TableCell>
							<TableCell align="right" width={100}>Cancelled</TableCell>
							<TableCell align="right" width={110}>Cancel rate</TableCell>
							<TableCell align="right" width={130}>Revenue lost</TableCell>
							<TableCell align="right" width={90}>Share</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{visible.map((p) => (
							<TableRow key={p.partner} hover>
								<TableCell>{p.partner}</TableCell>
								<TableCell align="right">{fmtNum(p.total)}</TableCell>
								<TableCell align="right">{fmtNum(p.cancelled)}</TableCell>
								<TableCell align="right">
									<Chip size="small" variant="filled"
									      color={rateColor(p.rate_pct)}
									      label={fmtPct(p.rate_pct)}
									      sx={{ fontWeight: 700, fontSize: 12 }}/>
								</TableCell>
								<TableCell align="right">
									{p.revenue_lost > 0
										? <b>{fmtMoney(p.revenue_lost)}</b>
										: <span style={{ opacity: 0.4 }}>—</span>}
								</TableCell>
								<TableCell align="right">{fmtPct(p.share_pct)}</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
		</Paper>
	)
}
